/**
 * woSampleUploadRows.ts — normalize every accepted upload format into the ONE
 * row shape the work-order sample upload reconciles (2026-09-10):
 *
 *   - flat CSV rows (woUploadParser's NormalizedWoRow — one line per test) are
 *     regrouped per sample: same Location ID + Sample Reference → one row with
 *     N test requests (a repeated test on the same sample becomes quantity N);
 *   - intake workbook / KeyLAB rows (PublicIntakeSample, already per sample
 *     with tests × quantity + params) map 1:1 — stresses / notes become the
 *     line's instructions the same way the intake → WO conversion writes them.
 *
 * Pure; unit-tested in woSampleUploadRows.test.ts.
 */
import type { NormalizedWoRow } from './woUploadParser';
import type { PublicIntakeSample } from '../types/intake';
import type { WoSampleUploadRowInput, WoSampleUploadTestInput } from '../types/workOrderSampleUpload';

const numOrNull = (raw: string | number | null | undefined): number | null => {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const up = (s: string | null | undefined): string => (s ?? '').trim().toUpperCase();

/** Flat CSV lines → per-sample rows. Order of first appearance is kept. */
export function csvRowsToUploadRows(rows: NormalizedWoRow[]): WoSampleUploadRowInput[] {
  const out: WoSampleUploadRowInput[] = [];
  const index = new Map<string, WoSampleUploadRowInput>();
  for (const r of rows) {
    const key = `${up(r.Location)}|${up(r.SampleReference)}`;
    let row = index.get(key);
    if (!row) {
      row = {
        locationId: r.Location?.trim() || null,
        sampleRef: r.SampleReference?.trim() || null,
        topDepth: numOrNull(r.DepthTop),
        baseDepth: numOrNull(r.DepthBase),
        sampleType: r.Type?.trim() || null,
        tests: [],
      };
      index.set(key, row);
      out.push(row);
    }
    const testName = r.TestName.trim();
    // Same test twice on one sample with identical per-line fields → quantity 2;
    // differing instructions / specimen refs stay as separate requests so
    // nothing typed in the file is lost.
    const same = row.tests.find((t) =>
      up(t.testName) === up(testName)
      && (t.instructions ?? '') === (r.Instructions?.trim() ?? '')
      && (t.specimenRef ?? '') === (r.SpecimenRef?.trim() ?? '')
      && (t.priority ?? '') === (r.Priority?.trim() ?? ''));
    if (same) { same.quantity = (same.quantity ?? 1) + 1; continue; }
    row.tests.push({
      testName,
      quantity: 1,
      instructions: r.Instructions?.trim() || null,
      specimenRef: r.SpecimenRef?.trim() || null,
      priority: r.Priority?.trim() || null,
    });
  }
  return out;
}

/** The per-line note the intake → WO conversion writes for a test's params. */
export function intakeParamsNote(params: PublicIntakeSample['tests'][number]['params']): string | null {
  if (!params) return null;
  const parts: string[] = [];
  if (params.stressesKpa && params.stressesKpa.length > 0) {
    parts.push(`Direct Shear normal stresses (kPa): ${params.stressesKpa.join(', ')}`);
  }
  if (params.confiningPressureMPa != null) {
    parts.push(`Confining pressure (MPa): ${params.confiningPressureMPa}`);
  }
  if (params.note) parts.push(params.note);
  return parts.length > 0 ? parts.join('\n') : null;
}

/** Confirmed intake-import samples (our workbook or KeyLAB) → upload rows. */
export function intakeSamplesToUploadRows(samples: PublicIntakeSample[]): WoSampleUploadRowInput[] {
  return samples.map((s) => ({
    locationId: s.locationId?.trim() || null,
    sampleRef: s.sampleRef?.trim() || null,
    topDepth: s.topDepth,
    baseDepth: s.baseDepth,
    sampleType: s.sampleType?.trim() || null,
    tests: s.tests.map((t): WoSampleUploadTestInput => ({
      testCatalogItemKey: t.testKey,
      quantity: Math.max(1, t.quantity || 1),
      instructions: intakeParamsNote(t.params),
    })),
  }));
}

/** Total requested test instances across rows (for button labels). */
export function countUploadTests(rows: WoSampleUploadRowInput[]): number {
  return rows.reduce((sum, r) => sum + r.tests.reduce((s, t) => s + Math.max(1, t.quantity ?? 1), 0), 0);
}
